import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, LayoutGrid, List, CalendarCheck, FileStack, RefreshCw, AlertTriangle, Euro, Trophy } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { makeApi, PROSPECT_TYPES, money, errMsg } from './crmConstants';
import { KanbanBoard, LeadList } from './KanbanBoard';
import { LeadDrawer } from './LeadDrawer';
import { OfferTemplatesDialog } from './OfferTemplatesDialog';
import { MyTasksDialog } from './MyTasksDialog';

const Stat = ({ icon: Icon, label, value, tone = 'text-white', testId }) => (
  <div className="bg-slate-800/60 border border-slate-700 rounded-xl px-4 py-3 flex items-center gap-3" data-testid={testId}>
    <Icon className="w-5 h-5 text-slate-400" />
    <div><p className={`text-lg font-bold ${tone}`}>{value}</p><p className="text-[11px] text-slate-400">{label}</p></div>
  </div>
);

const useCrmData = (api, filters) => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([api.get('/admin/leads', filters), api.get('/admin/crm/stats')]);
      setLeads(l.items || []); setStats(s);
    } catch { /* surfaced by empty state */ }
    setLoading(false);
  }, [api, filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/admin/crm/team').then(setTeam).catch(() => {}); }, [api]);
  return { leads, stats, team, loading, load };
};

export const LeadsCrm = ({ backendUrl, token, showToast, currentUser, focusLeadId }) => {
  const api = useMemo(() => makeApi(backendUrl, token), [backendUrl, token]);
  const [view, setView] = useState('kanban');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => { const t = setTimeout(() => setSearch(searchInput.trim()), 300); return () => clearTimeout(t); }, [searchInput]);
  const [owner, setOwner] = useState('');
  const [ptype, setPtype] = useState('');
  const filters = useMemo(() => ({ search: search || undefined, owner: owner || undefined, prospect_type: ptype || undefined }), [search, owner, ptype]);
  const { leads, stats, team, loading, load } = useCrmData(api, filters);
  const [openId, setOpenId] = useState(focusLeadId || null);
  const [tplOpen, setTplOpen] = useState(false);
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [lead, setLead] = useState(null);
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => { if (focusLeadId) setOpenId(focusLeadId); }, [focusLeadId]);
  useEffect(() => {
    if (!openId) { setLead(null); return; }
    api.get(`/admin/leads/${openId}`).then(setLead).catch(() => setOpenId(null));
  }, [openId, api]);

  const refresh = () => { load(); if (openId) api.get(`/admin/leads/${openId}`).then(setLead).catch(() => {}); };
  const move = async (id, status) => {
    if (status === 'lost' && window.prompt('Motivo della perdita (facoltativo):', '') === null) return;
    try { await api.patch(`/admin/leads/${id}`, { status }); load(); } catch (e) { showToast('error', errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="leads-crm">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">CRM Lead</h2>
          <p className="text-sm text-slate-400">Pipeline, azioni fatte, prossimi passi e offerte. Trascina le card per cambiare fase.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setAgendaOpen(true)} className="bg-slate-700 hover:bg-slate-600" data-testid="crm-agenda-btn"><CalendarCheck className="w-4 h-4 mr-2" /> Agenda</Button>
          <Button onClick={() => setTplOpen(true)} className="bg-slate-700 hover:bg-slate-600" data-testid="crm-templates-btn"><FileStack className="w-4 h-4 mr-2" /> Template offerte</Button>
          <Button onClick={load} className="bg-slate-700 hover:bg-slate-600" data-testid="crm-refresh-btn"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={AlertTriangle} label="Attività in ritardo" value={stats.tasks_overdue} tone={stats.tasks_overdue ? 'text-rose-400' : 'text-white'} testId="crm-stat-overdue" />
          <Stat icon={CalendarCheck} label="Da fare oggi" value={stats.tasks_today} testId="crm-stat-today" />
          <Stat icon={Euro} label="Valore pipeline aperta" value={money(stats.pipeline_value)} testId="crm-stat-value" />
          <Stat icon={Trophy} label="Offerte accettate / in attesa" value={`${stats.offers_accepted} / ${stats.offers_sent}`} tone="text-emerald-300" testId="crm-stat-offers" />
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Cerca nome, email, azienda…" className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm" data-testid="crm-search" />
        </div>
        <select value={owner} onChange={(e) => setOwner(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg text-white text-sm px-3 py-2" data-testid="crm-owner-filter">
          <option value="">Tutti i responsabili</option>
          <option value="me">Assegnati a me</option>
          <option value="none">Non assegnati</option>
          {isAdmin && team.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
        </select>
        <select value={ptype} onChange={(e) => setPtype(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg text-white text-sm px-3 py-2" data-testid="crm-type-filter">
          <option value="">Tutti i tipi</option>
          {PROSPECT_TYPES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <div className="flex bg-slate-800 border border-slate-700 rounded-lg p-0.5">
          <button onClick={() => setView('kanban')} className={`px-2.5 py-1.5 rounded ${view === 'kanban' ? 'bg-amber-500 text-slate-950' : 'text-slate-300'}`} data-testid="crm-view-kanban" title="Kanban"><LayoutGrid className="w-4 h-4" /></button>
          <button onClick={() => setView('list')} className={`px-2.5 py-1.5 rounded ${view === 'list' ? 'bg-amber-500 text-slate-950' : 'text-slate-300'}`} data-testid="crm-view-list" title="Lista"><List className="w-4 h-4" /></button>
        </div>
      </div>

      {view === 'kanban'
        ? <KanbanBoard leads={leads} onOpen={(l) => setOpenId(l.id)} onMove={move} />
        : <LeadList leads={leads} onOpen={(l) => setOpenId(l.id)} />}

      {lead && (
        <LeadDrawer lead={lead} api={api} team={team} currentUser={currentUser} showToast={showToast} onClose={() => setOpenId(null)} onChanged={refresh} />
      )}
      <OfferTemplatesDialog open={tplOpen} onClose={() => setTplOpen(false)} api={api} showToast={showToast} isAdmin={isAdmin} />
      <MyTasksDialog open={agendaOpen} onClose={() => { setAgendaOpen(false); load(); }} api={api} isAdmin={isAdmin} onOpenLead={(id) => { setAgendaOpen(false); setOpenId(id); }} />
    </div>
  );
};
